import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, ArrowRight, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "specialist", ref: refCode });
  const [busy, setBusy] = useState(false);
  const [referrer, setReferrer] = useState(null);

  useEffect(() => {
    if (refCode) {
      api.get("/referrals/validate", { params: { code: refCode } })
        .then((r) => { if (r.data.valid) setReferrer(r.data.referrer_name); })
        .catch(() => {});
    }
  }, [refCode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success(referrer ? `Account created ,  welcome via ${referrer}` : "Account created");
      navigate("/app/dashboard");
    } catch (err) {
      // BLOCK 74 ,  a 422 body has `detail` as an ARRAY of pydantic error
      // dicts, not a string. Passing that array directly into toast.error
      // (which renders in JSX) throws "Objects are not valid as a React
      // child". Normalize to a plain string.
      const raw = err?.response?.data?.detail;
      let msg = "Registration failed";
      if (typeof raw === "string") {
        msg = raw;
      } else if (Array.isArray(raw) && raw.length > 0) {
        msg = raw.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
      } else if (raw && typeof raw === "object") {
        msg = raw.msg || JSON.stringify(raw);
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070A] text-slate-100 relative overflow-hidden flex items-center justify-center px-6">
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[300px] w-[600px] rounded-full bg-emerald-500/10 blur-[120px]" />

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          <div className="h-8 w-8 rounded-sm bg-emerald-500 flex items-center justify-center">
            <Building2 className="h-4 w-4 text-black" strokeWidth={2.5} />
          </div>
          <span className="font-heading font-semibold">Acquigine</span>
        </Link>

        {referrer && (
          <div className="ae-card p-3 mb-4 border-emerald-500/40 flex items-center gap-2" data-testid="referrer-banner">
            <Gift className="h-4 w-4 text-emerald-400" />
            <span className="text-xs text-slate-300">Invited by <strong className="text-emerald-400">{referrer}</strong></span>
          </div>
        )}

        <div className="ae-card p-8">
          <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Create account</div>
          <h1 className="font-heading text-3xl tracking-tight">Get started.</h1>
          <p className="text-slate-400 mt-1 text-sm">Free trial · No credit card required.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <Field label="Full name" testid="register-name">
              <input
                data-testid="register-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="ae-input"
              />
            </Field>
            <Field label="Email">
              <input
                data-testid="register-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="ae-input"
              />
            </Field>
            <Field label="Password (min 6)">
              <input
                data-testid="register-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
                className="ae-input"
              />
            </Field>
            <Field label="Role">
              <select
                data-testid="register-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="ae-input"
              >
                <option value="specialist">Acquisition Specialist</option>
                <option value="investor">Investor</option>
              </select>
            </Field>
            <Field label="Referral code (optional)">
              <input
                data-testid="register-ref"
                value={form.ref}
                onChange={(e) => setForm({ ...form, ref: e.target.value.toUpperCase() })}
                placeholder="ABCD1234"
                className="ae-input font-mono uppercase tracking-wider"
              />
            </Field>

            <button
              type="submit"
              disabled={busy}
              data-testid="register-submit"
              className="btn-primary w-full inline-flex items-center justify-center gap-2 mt-2"
            >
              {busy ? "Creating…" : <>Create account <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <div className="mt-5 text-sm text-slate-400 text-center">
            Already have one?{" "}
            <Link to="/login" data-testid="register-to-login" className="text-emerald-400 hover:text-emerald-300">
              Sign in
            </Link>
          </div>
        </div>
      </div>

      <style>{`.ae-input { width: 100%; background: #0E1116; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; padding: 0.625rem 0.75rem; font-size: 0.875rem; outline: none; transition: all 200ms; }
.ae-input:focus { border-color: rgba(16,185,129,0.6); box-shadow: 0 0 0 1px rgba(16,185,129,0.4); }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
